const EXCERPT_AREA_SELECTOR = ".rc-excerpt-area";
const WHITESPACE = /\s/;
const closestExcerptArea = (node) => {
    const element = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
    return element?.closest(EXCERPT_AREA_SELECTOR) ?? null;
};
const collectTextNodes = (area) => {
    const walker = area.ownerDocument.createTreeWalker(area, NodeFilter.SHOW_TEXT);
    const nodes = [];
    for (let node = walker.nextNode(); node; node = walker.nextNode())
        nodes.push(node);
    return nodes;
};
// An element boundary sits between child nodes, so the snapped offset is always a node's near edge.
const snapToTextNode = (nodes, container, offset, forward) => {
    if (container.nodeType === Node.TEXT_NODE) {
        const node = container;
        return nodes.includes(node) ? { node, offset } : null;
    }
    const boundary = container.ownerDocument.createRange();
    boundary.setStart(container, offset);
    boundary.collapse(true);
    if (forward) {
        for (const node of nodes) {
            if (boundary.comparePoint(node, node.length) > 0)
                return { node, offset: 0 };
        }
        return null;
    }
    for (let index = nodes.length - 1; index >= 0; index--) {
        const node = nodes[index];
        if (boundary.comparePoint(node, 0) < 0)
            return { node, offset: node.length };
    }
    return null;
};
const advancePastWhitespace = (nodes, position) => {
    let index = nodes.indexOf(position.node);
    let offset = position.offset;
    while (index >= 0 && index < nodes.length) {
        const text = nodes[index].data;
        while (offset < text.length && WHITESPACE.test(text[offset]))
            offset++;
        if (offset < text.length)
            return { node: nodes[index], offset };
        index++;
        offset = 0;
    }
    return null;
};
const retractPastWhitespace = (nodes, position) => {
    let index = nodes.indexOf(position.node);
    let offset = position.offset;
    while (index >= 0) {
        const text = nodes[index].data;
        while (offset > 0 && WHITESPACE.test(text[offset - 1]))
            offset--;
        if (offset > 0)
            return { node: nodes[index], offset };
        index--;
        offset = index >= 0 ? nodes[index].data.length : 0;
    }
    return null;
};
// Ramius reads the token index off `startContainer.previousSibling`, so a boundary that is not on a real character names the wrong `rc-e-i` token.
export const normalizeExcerptSelection = (selection) => {
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed)
        return false;
    const range = selection.getRangeAt(0);
    const area = closestExcerptArea(range.startContainer);
    // A start outside every area makes the engine anchor at token 0, which is already the right token.
    if (!area)
        return false;
    // The engine excerpts only the start area's slice, so an end past the area is already trimmed for us.
    const endInArea = area.contains(range.endContainer);
    // A cross-area drag the engine rejects outright, so normalizing it would be wasted work.
    if (!endInArea && closestExcerptArea(range.endContainer))
        return false;
    const nodes = collectTextNodes(area);
    const rawStart = snapToTextNode(nodes, range.startContainer, range.startOffset, true);
    if (!rawStart)
        return false;
    const start = advancePastWhitespace(nodes, rawStart);
    if (!start)
        return false;
    const rawEnd = endInArea ? snapToTextNode(nodes, range.endContainer, range.endOffset, false) : null;
    const end = rawEnd && retractPastWhitespace(nodes, rawEnd);
    if (endInArea && !end)
        return false;
    const endNode = end?.node ?? range.endContainer;
    const endOffset = end?.offset ?? range.endOffset;
    const unchanged = start.node === range.startContainer &&
        start.offset === range.startOffset &&
        endNode === range.endContainer &&
        endOffset === range.endOffset;
    if (unchanged)
        return false;
    const normalized = area.ownerDocument.createRange();
    normalized.setStart(start.node, start.offset);
    normalized.setEnd(endNode, endOffset);
    // Whitespace-only drag: nothing excerptable, so keep the user's range and let the engine reject it.
    if (normalized.collapsed)
        return false;
    selection.removeAllRanges();
    selection.addRange(normalized);
    return true;
};
